import * as React from 'react';

export type FeatureFlags = {
    actionButtons: {
        download: boolean;
    };
};

// Type for nested flag paths like ['actionButtons', 'download']
type FlagPath = ['actionButtons', 'download'];
type FlagKey = keyof FeatureFlags;

// Simple feature flags provider compatible with React 18
const FlagsContext = React.createContext<FeatureFlags | undefined>(undefined);

export const FlagsProvider: React.FC<{ flags: FeatureFlags; children: React.ReactNode }> = ({
    flags,
    children,
}) => {
    return <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>;
};

// Helper to get nested flag value
const getFlagValue = (flags: FeatureFlags, path: FlagPath | [FlagKey]): boolean => {
    if (path.length === 1) {
        const key = path[0] as FlagKey;
        const value = flags[key];
        return typeof value === 'boolean' ? value : false;
    }
    const [first, second] = path as FlagPath;
    if (first in flags && typeof flags[first] === 'object') {
        const nested = flags[first] as Record<string, any>;
        return nested[second] === true;
    }
    return false;
};

export const Flag: React.FC<{
    name: FlagPath | [FlagKey];
    render?: () => React.ReactNode;
    children?: React.ReactNode;
}> = ({ name, render, children }) => {
    const flags = React.useContext(FlagsContext);
    if (!flags) {
        throw new Error('Flag must be used within FlagsProvider');
    }

    const isEnabled = getFlagValue(flags, name);

    if (isEnabled) {
        return <>{render ? render() : children}</>;
    }

    return null;
};

export const useFlag = (name: FlagPath | [FlagKey]): boolean => {
    const flags = React.useContext(FlagsContext);
    if (!flags) {
        throw new Error('useFlag must be used within FlagsProvider');
    }
    return getFlagValue(flags, name);
};
